// src/pages/ExpenseList.js
import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Filesystem, Directory, Encoding } from "@capacitor/filesystem";
import { Share } from "@capacitor/share";
import { Capacitor } from "@capacitor/core";
import { Toast } from "@capacitor/toast";
import API_URL from "../config"; // URL du backend
import {
  FaCalendarAlt,
  FaBox,
  FaFileExcel,
  FaFilter,
  FaTrash,
} from "react-icons/fa";
import * as XLSX from "xlsx";

/* =========================
   Utils : parse & format
   ========================= */
const parseFrNumber = (value) => {
  if (value == null) return 0;
  if (typeof value === "number" && isFinite(value)) return value;

  let s = String(value)
    .replace(/\u00A0/g, " ")
    .replace(/fcfa/gi, "")
    .replace(/[^\d.,\- ]/g, "")
    .trim();

  if (s.includes(",") && s.includes(".")) {
    s = s.replace(/\./g, "").replace(",", ".");
  } else if (s.includes(",")) {
    s = s.replace(",", ".");
  } else {
    s = s.replace(/ /g, "");
  }

  const n = parseFloat(s);
  return isNaN(n) ? 0 : n;
};

const formatFrCurrency = (n) => {
  const v = parseFrNumber(n);
  const str = new Intl.NumberFormat("fr-FR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(v);
  return str.replace(/\u00A0/g, " ") + " FCFA";
};

const formatFrInt = (n) => {
  const v = Math.round(parseFrNumber(n));
  const str = new Intl.NumberFormat("fr-FR", {
    maximumFractionDigits: 0,
  }).format(v);
  return str.replace(/\u00A0/g, " ");
};

const formatDateFR = (d) => {
  const date = d ? new Date(d) : null;
  return date && !isNaN(date) ? date.toLocaleDateString("fr-FR") : "-";
};

/* =========================
   Composant Dialogue
   ========================= */
function Dialog({ open, title, message, onClose, onConfirm, confirmText = "OK", cancelText }) {
  if (!open) return null;

  return (
    <div style={dialogStyles.overlay}>
      <div style={dialogStyles.dialog}>
        <h3 style={dialogStyles.title}>{title}</h3>
        <p style={dialogStyles.message}>{message}</p>
        <div style={dialogStyles.actions}>
          {cancelText && (
            <button onClick={onClose} style={dialogStyles.cancelBtn}>
              {cancelText}
            </button>
          )}
          {onConfirm && (
            <button onClick={onConfirm} style={dialogStyles.confirmBtn}>
              {confirmText}
            </button>
          )}
          {!onConfirm && !cancelText && (
            <button onClick={onClose} style={dialogStyles.confirmBtn}>
              Fermer
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/* =========================
   Page principale
   ========================= */
export default function ExpenseList({ entrepriseId}) { // siteId retiré du destructuring si présent
  const [expenses, setExpenses] = useState([]);

  // Filtres
  const [dayMode, setDayMode] = useState("all");
  const [selectedDate, setSelectedDate] = useState(() =>
    new Date().toISOString().slice(0, 10)
  );
  const [monthMode, setMonthMode] = useState("all");
  const [yearMode, setYearMode] = useState("all");
// 🔍 Filtre par nom
const [searchName, setSearchName] = useState("");
  const [availableMonths, setAvailableMonths] = useState([]);
  const [availableYears, setAvailableYears] = useState([]);

  // Dialogues
  const [dialog, setDialog] = useState({
    open: false,
    title: "",
    message: "",
    onConfirm: null,
    confirmText: "",
    cancelText: "",
  });

  // Exporting state (évite double press)
  const [isExporting, setIsExporting] = useState(false);

  // 📱 Détection mobile UI (taille écran)
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth <= 768 : false
  );
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  // Vérif plateforme au montage (debug)
  useEffect(() => {
    try {
      const platform = Capacitor.getPlatform();
      console.log("[Mount] Capacitor platform:", platform);
      Toast.show({ text: `Platform: ${platform}` }).catch(() => {});
    } catch (e) {
      console.warn("[Mount] Capacitor platform check failed:", e);
    }
  }, []);

  // 📡 Charger les dépenses
  useEffect(() => {
    if (!entrepriseId) return;

    fetch(`${API_URL}/api/depenses/${entrepriseId}/`)
      .then((res) => res.json())
      .then((data) => {
        const arr = Array.isArray(data) ? data : [];
        setExpenses(arr);

        const months = [
          ...new Set(
            arr
              .map((e) => new Date(e.date))
              .filter((d) => d && !isNaN(d))
              .map((d) => d.getMonth() + 1)
          ),
        ].sort((a, b) => a - b);

        const years = [
          ...new Set(
            arr
              .map((e) => new Date(e.date))
              .filter((d) => d && !isNaN(d))
              .map((d) => d.getFullYear())
          ),
        ].sort((a, b) => a - b);

        setAvailableMonths(months);
        setAvailableYears(years);
      })
      .catch(() => {
        setDialog({
          open: true,
          title: "Erreur",
          message: "Impossible de charger les dépenses.",
        });
      });
  }, [entrepriseId]);

  const today = useMemo(() => new Date(), []);

  const filteredExpenses = useMemo(() => {
  return expenses.filter((exp) => {
    const d = new Date(exp?.date);
    if (!(d && !isNaN(d))) return false;

    const sameDay = (a, b) => a.toDateString() === b.toDateString();

    // 📅 FILTRES EXISTANTS
    if (dayMode === "today" && !sameDay(d, today)) return false;

    if (dayMode === "custom" && selectedDate) {
      const sd = new Date(selectedDate);
      if (!sameDay(d, sd)) return false;
    }

    if (monthMode === "thisMonth") {
      if (d.getMonth() + 1 !== today.getMonth() + 1) return false;
    } else if (!isNaN(monthMode)) {
      if (d.getMonth() + 1 !== Number(monthMode)) return false;
    }

    if (yearMode === "thisYear") {
      if (d.getFullYear() !== today.getFullYear()) return false;
    } else if (!isNaN(yearMode)) {
      if (d.getFullYear() !== Number(yearMode)) return false;
    }

    // 🔥 NOUVEAU FILTRE PAR NOM
    if (searchName.trim() !== "") {
      const nom = (exp.nom || "").toLowerCase();
      const recherche = searchName.toLowerCase();

      if (!nom.includes(recherche)) return false;
    }

    return true;
  });
}, [expenses, dayMode, selectedDate, monthMode, yearMode, today, searchName]);
  const totals = useMemo(() => {
    const qty = filteredExpenses.reduce(
      (s, e) => s + parseFrNumber(e.quantite),
      0
    );
    const amount = filteredExpenses.reduce(
      (s, e) => s + parseFrNumber(e.montant),
      0
    );
    return { qty, amount };
  }, [filteredExpenses]);

  const monthLabel = (m) =>
    new Date(2000, m - 1, 1).toLocaleString("fr-FR", { month: "long" });

  const filterSummary = useMemo(() => {
    const dayText =
      dayMode === "today"
        ? "Aujourd’hui"
        : dayMode === "custom" && selectedDate
        ? new Date(selectedDate).toLocaleDateString("fr-FR")
        : "Tous les jours";

    const monthText =
      monthMode === "thisMonth"
        ? "Ce mois"
        : monthMode === "all"
        ? "Tous les mois"
        : `${monthLabel(Number(monthMode))}`;

    const yearText =
      yearMode === "thisYear"
        ? "Cette année"
        : yearMode === "all"
        ? "Toutes les années"
        : String(yearMode);

    return `${dayText} • ${monthText} • ${yearText}`;
  }, [dayMode, selectedDate, monthMode, yearMode]);

  /* =========================
     Export Excel robuste (web + native)
     ========================= */
  const downloadExcel = useCallback(
    async (e) => {
      if (e && typeof e.preventDefault === "function") e.preventDefault();

      if (isExporting) return;
      setIsExporting(true);

      try {
        const rows = filteredExpenses.map((exp) => [
          exp.nom || "",
          "",
          parseFrNumber(exp.quantite),
          "",
          parseFrNumber(exp.prix_unitaire),
          "",
          parseFrNumber(exp.montant),
          "",
          formatDateFR(exp.date),
        ]);

        const header = [
          "Nom", "", "Quantité", "", "Prix Unitaire (FCFA)", "",
          "Montant (FCFA)", "", "Date",
        ];

        const totalRow = ["TOTAL", "", totals.qty, "", "", "", totals.amount, "", ""];

        const wsData = [header, ...rows, totalRow];
        const ws = XLSX.utils.aoa_to_sheet(wsData);

        ws["!cols"] = [
          { wch: 25 },
          { wch: 3 },
          { wch: 12 },
          { wch: 3 },
          { wch: 18 },
          { wch: 3 },
          { wch: 18 },
          { wch: 3 },
          { wch: 15 },
        ];

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Dépenses");

        const filename = `depenses-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-")}.xlsx`;

        const platform = Capacitor.getPlatform();

        if (platform === "web") {
          const arrayBuf = XLSX.write(wb, { type: "array", bookType: "xlsx" });
          const blob = new Blob([arrayBuf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = filename;
          a.style.display = "none";
          document.body.appendChild(a);
          a.click();
          a.remove();
          URL.revokeObjectURL(url);
          try { await Toast.show({ text: "Téléchargement démarré" }); } catch {}
          setIsExporting(false);
          return;
        }

        const base64 = XLSX.write(wb, { type: "base64", bookType: "xlsx" });
        const isAndroid = platform === "android";
        const isIos = platform === "ios";

        if (isAndroid) {
          try {
            const writePath = `Download/${filename}`;
            const saved = await Filesystem.writeFile({
              path: writePath,
              data: base64,
              directory: Directory.ExternalStorage,
              encoding: Encoding.Base64,
            });

            try { await Toast.show({ text: "Fichier enregistré dans Téléchargements" }); } catch {}
            const uriRes = await Filesystem.getUri({ directory: Directory.ExternalStorage, path: writePath });
            await Share.share({ title: "Dépenses", text: "Fichier enregistré", url: uriRes.uri });
            setIsExporting(false);
            return;
          } catch (err) {
            console.warn("[ExcelExport] ⚠ Android write/share failed:", err);
          }
        }

        const targetDir = isIos ? Directory.Documents : Directory.Cache;
        const savedCache = await Filesystem.writeFile({
          path: filename,
          data: base64,
          directory: targetDir,
          encoding: Encoding.Base64,
        });
        const uriRes = await Filesystem.getUri({ directory: targetDir, path: filename });
        await Share.share({ title: "Dépenses", text: "Fichier prêt à partager", url: uriRes.uri });
        try { await Toast.show({ text: "Fichier prêt à partager" }); } catch {}
      } catch (err) {
        console.error("[ExcelExport] ❌ Erreur globale :", err);
        setDialog({ open: true, title: "Erreur export", message: err?.message || String(err) });
      } finally {
        setIsExporting(false);
      }
    },
    [filteredExpenses, totals, isExporting]
  );

  const handleDownloadClick = useCallback(
    async (event) => {
      try { await Toast.show({ text: "Export : préparation..." }); } catch {}
      await downloadExcel(event);
    },
    [downloadExcel]
  );
  // 🔥 AJOUT ARCHIVE : état pour blocage et indication
  const [isArchiving, setIsArchiving] = useState(false);

  // Détermine si un filtre est appliqué (autre que 'all')
  const isFilterApplied = useMemo(() => {
    const dayApplied = dayMode !== "all";
    const monthApplied = monthMode !== "all";
    const yearApplied = yearMode !== "all";
    // if dayMode is custom but selectedDate is equal to today and dayMode==="custom", consider applied anyway
    return dayApplied || monthApplied || yearApplied;
  }, [dayMode, monthMode, yearMode]);

  const handleDelete = (id) => {
    if (!entrepriseId ) return;
    setDialog({
      open: true,
      title: "Confirmation",
      message: "Voulez-vous vraiment supprimer cette dépense ?",
      confirmText: "Supprimer",
      cancelText: "Annuler",
      onConfirm: async () => {
        try {
          // MODIF : Suppression de siteId dans l'URL
          const res = await fetch(`${API_URL}/api/depenses/${id}/${entrepriseId}/`, { method: "DELETE" });
          if (res.ok) {
            setExpenses((prev) => prev.filter((exp) => exp.id !== id));
            setDialog({ open: true, title: "Succès", message: "La dépense a été supprimée avec succès." });
          } else {
            setDialog({ open: true, title: "Erreur", message: "Échec de la suppression." });
          }
        } catch {
          setDialog({ open: true, title: "Erreur", message: "Impossible de supprimer la dépense." });
        }
      },
    });
  };

  // 🔥 AJOUT ARCHIVE : fonction d'archivage
  const handleArchive = useCallback(async () => {
    if (!entrepriseId) {
      setDialog({ open: true, title: "Erreur", message: "Entreprise manquante." });
      return;
    }
    if (!isFilterApplied || filteredExpenses.length === 0) {
      setDialog({ open: true, title: "Aucune action", message: "Aucune dépense à archiver pour ces filtres." });
      return;
    }

    setDialog({
      open: true,
      title: "Archiver les dépenses",
      message: `Vous allez archiver ${filteredExpenses.length} dépense(s) correspondant à votre filtre. Continuer ?`,
      confirmText: "Archiver",
      cancelText: "Annuler",
      onConfirm: async () => {
        setDialog({ open: false, title: "", message: "" });
        setIsArchiving(true);
        try {
          // MODIF : Suppression de siteId du body
          const body = { depenses: filteredExpenses, entrepriseId };

          const res = await fetch(`${API_URL}/api/archive-depenses/archive`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
          });

          if (!res.ok) {
            // Try to parse message
            let errMsg = `Erreur serveur (${res.status})`;
            try {
              const j = await res.json();
              errMsg = j?.message || errMsg;
            } catch {}
            throw new Error(errMsg);
          }

          // Si backend confirme le traitement, on nettoie localement
          try {
            // On suppose que backend a archivé + supprimé côté serveur.
            // Met à jour la liste locale pour enlever les dépenses archivées
            const archivedIds = filteredExpenses.map((e) => e.id);
            setExpenses((prev) => prev.filter((e) => !archivedIds.includes(e.id)));

            try { await Toast.show({ text: "Dépenses archivées avec succès." }); } catch {}
            setDialog({ open: true, title: "Succès", message: "Les dépenses ont été archivées." });
          } catch (e2) {
            console.warn("[Archive] update local failed:", e2);
            setDialog({ open: true, title: "Succès partiel", message: "Les dépenses ont été envoyées, mais la mise à jour locale a échoué." });
          }
        } catch (err) {
          console.error("[Archive] Erreur :", err);
          setDialog({ open: true, title: "Erreur", message: err?.message || "Impossible d'archiver les dépenses." });
        } finally {
          setIsArchiving(false);
        }
      },
    });
  }, [filteredExpenses, entrepriseId, isFilterApplied]);

  return (
    <>
      <Dialog
        open={dialog.open}
        title={dialog.title}
        message={dialog.message}
        confirmText={dialog.confirmText}
        cancelText={dialog.cancelText}
        onConfirm={dialog.onConfirm}
        onClose={() => setDialog({ ...dialog, open: false })}
      />

      <div style={isMobile ? styles.pageMobile : styles.page}>
        <div style={isMobile ? {} : styles.card}>
          <div style={styles.header}>
            <div>
              <h1 style={styles.title}>Dépenses</h1>
              <p style={styles.subtitle}>Suivi et filtrage par dates</p>
            </div>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              {/* Bouton Archiver : visible uniquement si un filtre est appliqué ET il existe des dépenses filtrées */}
              {isFilterApplied && filteredExpenses.length > 0 && (
                <button
                  type="button"
                  onClick={handleArchive}
                  disabled={isArchiving}
                  style={{
                    ...styles.archiveBtn,
                    pointerEvents: "auto",
                    zIndex: 50,
                    position: "relative",
                    opacity: isArchiving ? 0.6 : 1,
                    marginRight: 8,
                  }}
                >
                  {/* icône custom simple (texte suffisant), tu peux remplacer par une icône */}
                  📦 Archiver ({filteredExpenses.length})
                </button>
              )}

              <button
                type="button"
                onClick={handleDownloadClick}
                onTouchStart={handleDownloadClick}
                onPointerDown={handleDownloadClick}
                onMouseDown={handleDownloadClick}
                disabled={isExporting}
                style={{
                  ...styles.excelBtn,
                  pointerEvents: "auto",
                  zIndex: 50,
                  position: "relative",
                  opacity: isExporting ? 0.6 : 1,
                }}
              >
                <FaFileExcel style={{ marginRight: 8 }} />{" "}
                {isExporting ? "Export en cours..." : "Télécharger Excel"}
              </button>
            </div>
          </div>

          <div style={styles.summaryBar}>
            <div>
              <div style={styles.summaryTitle}>Aperçu</div>
              <div style={styles.summarySub}>{filterSummary}</div>
            </div>
            <div style={styles.kpis}>
              <div style={styles.kpi}>
                <div style={styles.kpiLabel}>Lignes</div>
                <div style={styles.kpiValue}>{filteredExpenses.length}</div>
              </div>
              <div style={styles.kpi}>
                <div style={styles.kpiLabel}>Quantité</div>
                <div style={styles.kpiValue}>{formatFrInt(totals.qty)}</div>
              </div>
              <div style={styles.kpi}>
                <div style={styles.kpiLabel}>Total</div>
                <div style={styles.kpiValue}>{formatFrCurrency(totals.amount)}</div>
              </div>
            </div>
          </div>

          {/* Filtres */}
          <div style={styles.filters}>
            <div style={styles.filterTitle}>
              <FaFilter /> Filtres
            </div>
            <div style={styles.filterRow}>
              {/* 🔍 Recherche par nom */}
<div style={styles.filterBlock}>
  <label style={styles.label}>Nom</label>
  <input
    type="text"
    placeholder="Rechercher une dépense..."
    value={searchName}
    onChange={(e) => setSearchName(e.target.value)}
    style={styles.select}
  />
</div>
              {/* Jour */}
              <div style={styles.filterBlock}>
                <label style={styles.label}>Jour</label>
                <select value={dayMode} onChange={(e) => setDayMode(e.target.value)} style={styles.select}>
                  <option value="all">Tous</option>
                  <option value="today">Aujourd’hui</option>
                  <option value="custom">Choisir une date</option>
                </select>
              </div>
              {dayMode === "custom" && (
                <div style={styles.filterBlock}>
                  <label style={styles.label}>Date</label>
                  <input type="date" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} style={styles.select} />
                </div>
              )}

              {/* Mois */}
              <div style={styles.filterBlock}>
                <label style={styles.label}>Mois</label>
                <select
                  value={monthMode}
                  onChange={(e) =>
                    setMonthMode(isNaN(e.target.value) ? e.target.value : Number(e.target.value))
                  }
                  style={styles.select}
                >
                  <option value="all">Tous</option>
                  <option value="thisMonth">Ce mois</option>
                  {availableMonths.map((m) => (
                    <option key={m} value={m}>
                      {monthLabel(m)}
                    </option>
                  ))}
                </select>
              </div>

              {/* Année */}
              <div style={styles.filterBlock}>
                <label style={styles.label}>Année</label>
                <select
                  value={yearMode}
                  onChange={(e) =>
                    setYearMode(isNaN(e.target.value) ? e.target.value : Number(e.target.value))
                  }
                  style={styles.select}
                >
                  <option value="all">Toutes</option>
                  <option value="thisYear">Cette année</option>
                  {availableYears.map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Tableau */}
          <div style={styles.tableWrap}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}><FaBox /> Nom</th>
                  <th style={styles.th}>Quantité</th>
                  <th style={styles.th}>Prix Unitaire</th>
                  <th style={styles.th}>Montant</th>
                  <th style={styles.th}><FaCalendarAlt /> Date</th>
                  <th style={styles.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredExpenses.length > 0 ? (
                  filteredExpenses.map((expense) => (
                    <tr key={expense.id} style={styles.tr}>
                      <td style={styles.td}>{expense.nom}</td>
                      <td style={{ ...styles.td, textAlign: "right" }}>{formatFrInt(expense.quantite)}</td>
                      <td style={{ ...styles.td, textAlign: "right" }}>{formatFrCurrency(expense.prix_unitaire)}</td>
                      <td style={{ ...styles.td, textAlign: "right" }}>{formatFrCurrency(expense.montant)}</td>
                      <td style={{ ...styles.td, textAlign: "center" }}>{formatDateFR(expense.date)}</td>
                      <td style={styles.td}>
                        <button onClick={() => handleDelete(expense.id)} style={styles.deleteBtn}>
                          <FaTrash />
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" style={styles.emptyCell}>Aucune dépense trouvée</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
}

/* =========================
   Styles (inchangés)
   ========================= */
const styles = {
  page: { minHeight: "100vh", padding: "16px", display: "flex", justifyContent: "center", background: "linear-gradient(135deg, #f9fafb 0%, #eef2ff 100%)" },
  pageMobile: { padding: "10px", background: "#fff" },
  card: { width: "100%", maxWidth: 1100, background: "#fff", borderRadius: 18, boxShadow: "0 10px 30px rgba(0,0,0,0.08)", overflow: "hidden" },
  header: { padding: "16px 20px", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 },
  title: { margin: 0, fontSize: 20, fontWeight: 800 },
  subtitle: { margin: 0, fontSize: 12, color: "#6b7280" },
  excelBtn: { display: "inline-flex", alignItems: "center", padding: "8px 12px", borderRadius: 10, background: "#047857", color: "#fff", border: "none", cursor: "pointer", fontWeight: 700, fontSize: 12, touchAction: "manipulation" },
  // 🔥 AJOUT ARCHIVE : style bouton archive
  archiveBtn: { display: "inline-flex", alignItems: "center", padding: "8px 12px", borderRadius: 10, background: "#0ea5a4", color: "#fff", border: "none", cursor: "pointer", fontWeight: 700, fontSize: 12, touchAction: "manipulation" },
  summaryBar: { padding: "12px 20px", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12, borderBottom: "1px solid #f3f4f6", background: "#fafafa" },
  summaryTitle: { fontSize: 12, fontWeight: 700 },
  summarySub: { fontSize: 11, color: "#6b7280" },
  kpis: { display: "flex", gap: 12, flexWrap: "wrap" },
  kpi: { flex: "1 1 120px", padding: "8px", background: "#fff", borderRadius: 10, border: "1px solid #e5e7eb", textAlign: "center" },
  kpiLabel: { fontSize: 11, color: "#6b7280" },
  kpiValue: { fontSize: 14, fontWeight: 800 },
  filters: { padding: 16, borderBottom: "1px solid #f3f4f6" },
  filterTitle: { fontWeight: 700, marginBottom: 10, display: "flex", gap: 6, fontSize: 12 },
  filterRow: { display: "flex", flexWrap: "wrap", gap: 12 },
  filterBlock: { display: "flex", flexDirection: "column", gap: 4 },
  label: { fontSize: 11, fontWeight: 600 },
  select: { padding: "8px", borderRadius: 8, border: "1px solid #e5e7eb", fontSize: 12 },
  tableWrap: { padding: 16, overflowX: "auto" },
  table: { width: "100%", borderCollapse: "collapse" },
  th: { padding: "10px", background: "#eef2ff", textAlign: "left", fontSize: 12 },
  td: { padding: "10px", borderBottom: "1px solid #f3f4f6", fontSize: 12 },
  tr: { background: "#fff" },
  emptyCell: { padding: 16, textAlign: "center", color: "#6b7280", fontSize: 12 },
  deleteBtn: { background: "transparent", border: "none", color: "#dc2626", cursor: "pointer", fontSize: 14 },
};

const dialogStyles = {
  overlay: { position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.4)", display: "flex", justifyContent: "center", alignItems: "center", zIndex: 9999 },
  dialog: { background: "#fff", padding: "20px", borderRadius: 12, maxWidth: 400, width: "90%", boxShadow: "0 8px 20px rgba(0,0,0,0.2)" },
  title: { margin: "0 0 10px", fontSize: 18, fontWeight: "bold" },
  message: { marginBottom: 20, fontSize: 15, color: "#333" },
  actions: { display: "flex", justifyContent: "flex-end", gap: "10px" },
  cancelBtn: { padding: "8px 16px", border: "none", borderRadius: 8, cursor: "pointer", fontSize: 14, background: "#ccc", color: "#000", fontWeight: "600" },
  confirmBtn: { padding: "8px 16px", border: "none", borderRadius: 8, cursor: "pointer", fontSize: 14, background: "#4CAF50", color: "#fff", fontWeight: "600" }, };